import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import config from '@iotv/config';
import { classDerivedDeviceEnabledTypes, getParentEdgeTypeId } from '@iotv/datamodel';
import { ErrDataReducer, getObjectHash, getZombieEdge, isEdge } from '@iotv/iotv-v3-types';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import { Alert, Box, Button, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import { RichTreeView, TreeItem2 } from '@mui/x-tree-view';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import { ContainerArbitrator } from '../../../../ArbitrationObjects/ContainerArbitrator/containerArbitrator';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { ConsumerDao } from '../../../../data/ConsumerDAO';
import { getUserToVisibleGroupsQueryDef } from '../../../../helpers/queryHelpers';
import { ActionIcons } from '../../../../Iconography';
import { Severity } from '../../../../types';
import { commonButtonProps } from '../../generic/ObjectView/DetailCard/Controls/common';
import { getAllBeneathCustomerQuery } from './queries';
const debug = process.env.REACT_APP_DEBUG && false;
/*************
 * Note: The component actually starts near line 200: {@link MoveBetweenSupportedParentEdges}
 * ***********/
// TODO: you can currently move a device from one entity into an entity that has a device attached.
// I think we need a better mechanism to determine what a valid link actually means
// Ideally, centralized and referenced by everything via helper functions. But hard-coded functions that check certain items for validity would be good too.
// Of course, this all needs to be venture extendable
// By default, objects will only be moved into containers that are the same type as where you're moving them from.
// There are times, however, when it is desireable to be able to move to multiple different types.
// This fn specifies additions
// TODO: one day this should probably extend from a much more specific and configurable edge type matrix. Manual for now.
//
// When moving from type outer key, types of inner key will also be allowed to move to listed types
const getOwnerAdditions = (typeToMove, sourceContainerType) => {
    const additions = {
        Customer: {
            Group: [typeToMove], // Groups under customers can move to subgroups of same supported type
        },
        Area: {
            Area: ['Property'], // Sub areas are allowed to be moved to properties
            Place: ['Property'],
        },
        Property: {
            Area: ['Area'],
            Place: ['Area'],
            Group: ['Area'],
        },
        Group: {
            Group: ['Customer'],
        },
    };
    const sourceKey = typeof sourceContainerType === 'object' ? sourceContainerType.type : sourceContainerType;
    const typeToMoveKey = typeof typeToMove === 'object' ? typeToMove.type : typeToMove;
    return additions[sourceKey]?.[typeToMoveKey];
};
//const canonicalOwnerOverrides: OwnerOverrides = {
//  Place: ['Area', 'Property'],
//  Area: ['Property', 'Area'],
//
//  Group_Customer: ['Customer'], // Remember groups are always able to go into themselves, but that's auto populated
//  Group_Device: ['Customer'],
//  Group_User: ['Customer'],
//  Group_Gateway: ['Customer'],
//
//  // Every group of entities can connect to customer
//  // TODO: does this work with the new grouper types?
//  ...classDerivedDeviceEnabledTypes.reduce((c: OwnerOverrides, deviceEnabledType: string) => {
//    c[`Group_${deviceEnabledType}`] = ['Customer'];
//    return c;
//  }, {}),
//};
/** Simple sorter function for tree items */
const treeItemSortFunc = (a, b) => {
    const type = a.type.localeCompare(b.type);
    const name = a.name?.localeCompare(b.name ?? '') ?? 0;
    const supportedType = a.supportedType?.localeCompare(b.supportedType ?? '') ?? 0;
    return type || supportedType || name;
};
/**
 * Recursively traverses children of nodes and turns it into a `TreeItem` structure for a `RichTreeView`
 *
 * Note: apart from the addition of a children list, the objects are still just `VOB`s, so you need to use `RichTreeView`'s `getItemId` and `getItemLabel` to have it work.
 *
 * @param nem - The node edge mapper to turn into tree view items
 * @param skipFirstLevel - If true, the apex nodes will not be part of the tree - the first row of children will be treated as the apex nodes. Helpful when you query on a customer but don't want them in the tree (because they'll always be the first node)
 */
const nodeEdgeMapperToTreeViewItems = (nem, skipFirstLevel = false) => {
    let apex = nem.getApexNodes();
    if (skipFirstLevel) {
        const newApex = [];
        apex.forEach((apexNode) => {
            const firstChildren = nem.getChildNodes(apexNode);
            firstChildren.forEach((c) => newApex.push(c));
        });
        apex = newApex;
    }
    //if (nodeFilter) apex = apex.filter(nodeFilter);
    const treeItems = [];
    const nodeToTreeItem = (node) => {
        const treeItem = { ...node, treeId: `${v4()}_${node.sk}` };
        const childNodes = nem.getChildNodes(node);
        if (childNodes.length >= 1) {
            treeItem.children = [];
            childNodes.forEach((childNode) => {
                treeItem.children.push(nodeToTreeItem(childNode));
            });
            //if (nodeFilter) treeItem.children = treeItem.children.filter(nodeFilter);
            treeItem.children.sort(treeItemSortFunc);
        }
        return treeItem;
    };
    apex.forEach((apexNode) => {
        treeItems.push(nodeToTreeItem(apexNode));
    });
    treeItems.sort(treeItemSortFunc);
    debug && console.log('NodeEdgeMapperToTreeViewItems', treeItems);
    return treeItems;
};
const getTypeString = (vob) => {
    if (typeof vob === 'object') {
        if (vob.type === 'Group') {
            return config.apps?.webUI?.groupedObjectTypes?.[vob.supportedType]?.collectionName ?? `${vob.supportedType} Group`;
        }
        else {
            return vob.type;
        }
    }
    else {
        return vob;
    }
};
/** Validator Helpers */
const isTypeValidForMove = (type, validTypes) => {
    for (const vt of validTypes) {
        if (typeof vt === 'string' && typeof type === 'string' && vt === type) {
            return true;
        }
        else if (typeof vt === 'object' && typeof type === 'object' && vt.supportedType === type.supportedType) {
            return true;
        }
    }
};
const nodeToGetBeneathType = (node) => {
    return node.supportedType ? { type: 'Group', supportedType: node.supportedType } : node.type;
};
/**
 * Tests if a node is a valid destination for moving
 * @param node - The node to test
 * @param validTargetTypes - The list of types that are valid to move into
 * @param validDestinationNem - A node edge mapper containing the tree of all possible destinations
 * @param nodesToMove - Nodes being moved
 * @param sourceContainer - The container nodes are being moved from
 */
const isNodeValidDestination = (node, validTargetTypes, destinationNem, nodesToMove, sourceContainer) => {
    // Is the node type valid?
    const type = nodeToGetBeneathType(node);
    if (!isTypeValidForMove(type, validTargetTypes))
        return {
            valid: false,
            message: `Can't move to ${node.name} (${getTypeString(node)}) because it is not a valid container of ${getTypeString(nodesToMove[0])}`,
        };
    // Is this node what we're moving form?
    if (node.sk === sourceContainer.sk)
        return { valid: false, message: `Selected items are already in ${node.name} (${getTypeString(node)})` };
    // Is this node what we're moving form?
    for (const nodeBeingMoved of nodesToMove) {
        if (node.sk === nodeBeingMoved.sk)
            return { valid: false, message: `Can't move to ${node.name} (${getTypeString(node)}) because it is one of the items being moved` };
    }
    // Is this node a child of one of the nodes to move?
    const targetChildSks = destinationNem?.getAscendents([node]).map((n) => n.sk);
    if (targetChildSks) {
        for (const objToMove of nodesToMove) {
            if (targetChildSks.includes(objToMove.sk))
                return { valid: false, message: `Can't move to ${node.name} (${getTypeString(node)}) because it is a child of one of the items being moved` };
        }
    }
    return { valid: true, message: '' };
};
/**
 * Populates dialogue that allows selection of where to move objects.
 * First populates a NodeEdgeMapper with full tree down to valid container objects (based on a query under the context customer)
 * Query is limited to current context customer only.
 *
 *
 *   1. Valid destination types are acquired
 *     1a. If we're moving a group, the valid types always includes
 *   2. A query fetches all nodes of valid destination type under the context customer
 *   3. The nodes are transformed into a tree structure and presented to the user
 *
 * Some helper functions above validates selection, preventing:
 * - Moving items into themselves
 * - Moving items into children of themselves
 * - Moving items to containers they are already inside
 * - Moving items into invalid destinations
 */
export const MoveBetweenSupportedParentEdges = ({ sourceContainer, objectsToMove, onlyVisibleGroups }) => {
    const navigate = useNavigate();
    const moveType = useMemo(() => {
        return nodeToGetBeneathType(objectsToMove[0]);
    }, [objectsToMove]);
    const [{ contextCustomer, applicationData, contextUser, UserFunctions: { setUserMessage, addObjectsToState, removeObjectsFromState }, },] = useContext(AppMainViewContext);
    /** The valid move types vary pretty distinctly if you're moving a group or a non group
     * If the thing we're moving is a group
     * - It can always go inside a group with the same supported type
     * - The edge type matrix can tell us what can contain GROUPS of any kind, but not
     *   -- Additionally, there is a hidden distinction between what OWNS a group (what it can be moved to) and what is just LINKED to the group. Edge type matrix doesn't tell us that.
     *   -- Because of this, I just hard code a matrix of Object Type: Allowed Supported Types above.
     *   -- TODO: Get this info into the edge type matrix PLEASE
     *
     * If the thing we're moving is not a group
     * - The edge type matrix tells us what can link to what, so we use that as valid groups.
     */
    const validDestinationTypes = useMemo(() => {
        let parentTypes = [nodeToGetBeneathType(sourceContainer)];
        //const ownerKey = typeof moveType === 'object' ? `${moveType.type}_${moveType.supportedType}` : moveType;
        //debug && console.log('MoveBetweenSupportedParentEdges: moved type key into canonical owners', ownerKey);
        const ownerAdditions = getOwnerAdditions(moveType, nodeToGetBeneathType(sourceContainer));
        debug &&
            console.log(`moveBetweenSupportedParentEdges: owner additions for `, moveType, `(source type `, nodeToGetBeneathType(sourceContainer), `)`, ownerAdditions);
        if (ownerAdditions !== undefined) {
            parentTypes = parentTypes.concat(ownerAdditions);
        }
        // Dedupe - I don't think we need to do this but I wrote it so might as well leave it in.
        parentTypes = parentTypes.reduce((uniqueTypes, parentType) => {
            if (typeof parentType === 'string') {
                if (!uniqueTypes.find((ut) => ut === parentType))
                    uniqueTypes.push(parentType);
            }
            else if (typeof parentType === 'object') {
                if (!uniqueTypes.find((ut) => typeof ut === 'object' && ut.type === parentType.type && ut.supportedType === parentType.supportedType))
                    uniqueTypes.push(parentType);
            }
            return uniqueTypes;
        }, []);
        debug && console.log('Valid Move Types', parentTypes);
        return parentTypes;
    }, [sourceContainer, moveType]);
    // Gets valid move targets based on parent links in Edge Type Matrix
    const [destinationTreeItems, setDestinationTreeItems] = useState(null);
    const [destinationNem, setDestinationNem] = useState(null);
    const visibleGroupsHash = useMemo(() => {
        const visibleGroups = contextUser ? applicationData?.applyBranchingQuery([contextUser], getUserToVisibleGroupsQueryDef()).getNodesOfType('Group') : null;
        getObjectHash(visibleGroups);
    }, [applicationData, contextUser]);
    useEffect(() => {
        if (!contextCustomer)
            return;
        debug && console.log('Valid Move Types', validDestinationTypes);
        //if (!validMoveTargetTypes.includes(sourceContainer.type)) {
        //  setValidMoveTree([]);
        //  setValidMoveNem(new NodeEdgeMapper<ValidBusinessObject>());
        //  return;
        //}
        ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextCustomer],
            query: getAllBeneathCustomerQuery(validDestinationTypes),
        }).then((res) => {
            debug && console.log('MoveBetweenSupportedParentEdges: get all beneath query res', res);
            const destNem = res.data;
            if (destNem) {
                if (onlyVisibleGroups && applicationData && contextUser) {
                    const visibleGroups = applicationData.applyBranchingQuery([contextUser], getUserToVisibleGroupsQueryDef()).getNodesOfType('Group');
                    // TODO: filter nem based on visible groups
                    for (const node of destNem.getContainedItems()) {
                        if (node.type === 'Group' &&
                            [...classDerivedDeviceEnabledTypes, 'Device', 'Gateway'].includes(node.supportedType) &&
                            !visibleGroups.find((vg) => vg.sk === node.sk && vg.pk === node.pk)) {
                            destNem.removeItem(node);
                        }
                    }
                }
                const treeItems = nodeEdgeMapperToTreeViewItems(destNem, !validDestinationTypes.includes('Customer'));
                setDestinationTreeItems(treeItems);
                setDestinationNem(res.data);
            }
        });
    }, [validDestinationTypes, contextCustomer, visibleGroupsHash]);
    const [selectedDestinationObject, setSelectedDestinationObject] = useState(null);
    const executeMove = async (destinationObject) => {
        setProcessingMove(true);
        const destination = ContainerArbitrator.getCanonicalContainerLinkForObject({ vob: destinationObject });
        let msg = 'No action taken';
        const currentEdges = await ConsumerDao.batchGet(objectsToMove.map((item) => getZombieEdge(sourceContainer, item)));
        if (!currentEdges) {
            setUserMessage({ id: v4(), type: 'MessageBar', severity: Severity.error, label: 'Move error!', content: 'Failed to find existing edges to move' });
            setProcessingMove(false);
            return;
        }
        const newEdges = currentEdges.filter(isEdge).map((item) => {
            msg = '';
            if (sourceContainer.type === destinationObject.type) {
                const { edgeTypeId, edgeAttributes } = item;
                return getZombieEdge(destinationObject, item, edgeTypeId, edgeAttributes);
            }
            else {
                return getZombieEdge(destinationObject, item, getParentEdgeTypeId(item.type, destinationObject.type));
            }
        });
        const res_putNewEdges = await ConsumerDao.batchPut({ Items: newEdges });
        if (res_putNewEdges.err) {
            msg = `Could not link items: ${res_putNewEdges.err.message}.`;
            setUserMessage({ id: v4(), type: 'MessageBar', severity: Severity.error, label: 'Move error!', content: msg });
            console.error(`MoveBetweenSupportedParentEdges failed to link (attempted ${newEdges.length} edges): `, res_putNewEdges.err);
        }
        else if (res_putNewEdges.data) {
            const res_unlink = await Promise.all(currentEdges.map(async (item) => ConsumerDao.deleteExact(item)));
            debug && console.log('MoveBetweenSupportedParentEdges: unlink res', JSON.stringify(res_unlink));
            const consolidatedUnlinkRes = ErrDataReducer(res_unlink);
            if (consolidatedUnlinkRes.err) {
                msg = `Could not unlink items: ${consolidatedUnlinkRes.err.message}.`;
                setUserMessage({ id: v4(), type: 'MessageBar', severity: Severity.error, label: 'Move error!', content: msg });
                console.error(`MoveBetweenSupportedParentEdges failed to delete (attempted ${currentEdges.length} edges): `, consolidatedUnlinkRes.err);
            }
            else {
                removeObjectsFromState(consolidatedUnlinkRes.data ?? []);
                addObjectsToState(res_putNewEdges.data.ProcessedItems ?? []);
                msg = `Moved ${newEdges.length} item${newEdges.length > 1 ? 's' : ''} from ${sourceContainer.name} to ${destinationObject.name}`;
                setUserMessage({ id: v4(), type: 'MessageBar', severity: Severity.success, label: 'Moved!', content: msg });
                navigate(destination);
            }
        }
        setProcessingMove(false);
    };
    const [processingMove, setProcessingMove] = useState(false);
    /** Various validator memos */
    const nodeValidity = useMemo(() => {
        if (!selectedDestinationObject)
            return { valid: false, message: 'No destination selected!' };
        if (!destinationNem)
            return { valid: false, message: 'No destinations available!' };
        return isNodeValidDestination(selectedDestinationObject, validDestinationTypes, destinationNem, objectsToMove, sourceContainer);
    }, [selectedDestinationObject, destinationNem, validDestinationTypes, objectsToMove, sourceContainer]);
    return (_jsx(Stack, { gap: 1, children: _jsxs(Box, { maxHeight: 600, children: [destinationTreeItems && (_jsxs(Stack, { gap: 1, children: [_jsx(RichTreeView, { items: destinationTreeItems, getItemId: (n) => n.treeId, getItemLabel: (n) => n.sk, slots: { item: TreeItem2 }, slotProps: {
                                item: {
                                    slots: {
                                        collapseIcon: ActionIcons.NavigationCollapse,
                                        expandIcon: ActionIcons.NavigationExpand,
                                        label: (l) => {
                                            const sk = l.children;
                                            const node = destinationNem?.skMapped[sk][sk];
                                            if (!node)
                                                return;
                                            const validNode = isNodeValidDestination(node, validDestinationTypes, destinationNem, objectsToMove, sourceContainer).valid;
                                            return _jsx(Typography, { sx: { color: validNode ? undefined : 'gray' }, children: node.name });
                                        },
                                    },
                                },
                            }, onSelectedItemsChange: (_, ids) => {
                                if (ids === null) {
                                    setSelectedDestinationObject(null);
                                    return;
                                }
                                const selectedSk = ids.split('_')[1];
                                const selectedNode = destinationNem?.skMapped[selectedSk][selectedSk];
                                if (selectedNode)
                                    setSelectedDestinationObject(selectedNode);
                            } }), destinationTreeItems.length === 0 && _jsx(Alert, { severity: "info", children: "No valid places to move selection" }), _jsxs(Stack, { direction: "row-reverse", gap: 1, children: [destinationTreeItems.length > 0 && (_jsx(Button, { startIcon: _jsx(MoveDownIcon, {}), disabled: destinationTreeItems.length === 0 || selectedDestinationObject === null || !nodeValidity.valid || processingMove, onClick: () => selectedDestinationObject && executeMove(selectedDestinationObject), ...commonButtonProps, sx: { ...commonButtonProps.sx, maxWidth: 300 }, children: selectedDestinationObject === null ? 'Move' : `Move to ${selectedDestinationObject.name}` })), !nodeValidity.valid && (_jsx(Alert, { severity: "warning", sx: { paddingBlock: 0 }, children: nodeValidity.message })), processingMove && _jsx(CircularProgress, {})] })] })), destinationTreeItems === null && _jsx(Skeleton, { variant: "rounded", height: 200 })] }) }));
};
